import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const zeroAuthSessionPath = "/api/zero-auth/session";
export const zeroAuthRefreshPath = "/api/zero-auth/refresh";

export const zeroAuthSessionSuccessSchema = z.object({
  token: z.string().min(1),
  expires_at: z.number().int().positive(),
});

export async function createZeroAuthSessionRequest({
  apiBaseUrl,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof zeroAuthSessionSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${zeroAuthSessionPath}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to create Zero auth session",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = zeroAuthSessionSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid Zero auth session response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function refreshZeroAuthSessionRequest({
  apiBaseUrl,
  zeroToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  zeroToken: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof zeroAuthSessionSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${zeroAuthRefreshPath}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${zeroToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to create Zero auth session",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = zeroAuthSessionSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid Zero auth refresh response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
